/**
 * Settings — single-page horizontal tab strip per the
 * `oneads-handoff_settings` design. Replaces the old two-column nav
 * layout.
 *
 * Each tab is a self-contained component under `tabs/`. Active tab
 * is in `?tab=X` URL query param so deep-links survive page reload
 * and back/forward.
 *
 * The 9 tabs map 1:1 to ST_TABS in screens/settings.jsx.
 */

import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Topbar } from "@/components/Topbar";
import { UserAlertsBell } from "@/components/UserAlertsBell";
import { hasAnyAppAccess } from "@/lib/roles";
import { useAuth } from "@/lib/useAuth";
import type { Role } from "@/lib/types";
import { AdAccountTab } from "./tabs/AdAccountTab";
import { AppSettingsTab } from "./tabs/AppSettingsTab";
import { BillingTab } from "./tabs/BillingTab";
import { IntegrationsTab } from "./tabs/IntegrationsTab";
import { NotificationsTab } from "./tabs/NotificationsTab";
import { OrganizationTab } from "./tabs/OrganizationTab";
import { OtherTab } from "./tabs/OtherTab";
import { ProfileTab } from "./tabs/ProfileTab";
import { RolesTeamTab } from "./tabs/RolesTeamTab";

type TabId =
  | "ad-account"
  | "integrations"
  | "roles"
  | "organization"
  | "billing"
  | "app-settings"
  | "notifications"
  | "profile"
  | "other";

const EVERYONE: readonly Role[] = [
  "admin",
  "ads_manager",
  "finance_manager",
  "viewer",
];
const ADMIN_ONLY: readonly Role[] = ["admin"];

type TabDef = {
  id: TabId;
  label: string;
  /** Роли, которые видят вкладку. */
  roles: readonly Role[];
  /** ★ Вкладка про приложения. Финменеджеру она открывается только когда
   *  ему выдано хоть одно приложение: без рекламы там нечего смотреть, а
   *  с выданной он не должен оказаться ниже обычного участника (решение
   *  владельца). Обычному участнику вкладка видна всегда — у него нет
   *  другого смысла существования, и пустой список апп ему объясняет
   *  больше, чем исчезнувшая вкладка. */
  appScoped?: boolean;
};

const TABS: TabDef[] = [
  { id: "ad-account", label: "Ad Account", roles: EVERYONE, appScoped: true },
  { id: "integrations", label: "Integrations", roles: ADMIN_ONLY },
  { id: "roles", label: "Roles / Team", roles: ADMIN_ONLY },
  { id: "organization", label: "Organization", roles: EVERYONE },
  { id: "billing", label: "Billing", roles: EVERYONE },
  { id: "app-settings", label: "App Settings", roles: EVERYONE, appScoped: true },
  { id: "notifications", label: "Notifications", roles: EVERYONE },
  { id: "profile", label: "Profile", roles: EVERYONE },
  { id: "other", label: "Other", roles: EVERYONE },
];

export function SettingsLayout() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();

  // Видимость считается от роли И от выданных приложений — оба ответа
  // приходят с сервера в /auth/me. Вкладка вне списка не рисуется в
  // стрипе и не открывается по прямой ссылке.
  const visible = useMemo(
    () =>
      user
        ? TABS.filter(
            (t) =>
              t.roles.includes(user.role) &&
              (!t.appScoped ||
                user.role !== "finance_manager" ||
                hasAnyAppAccess(user)),
          )
        : [],
    [user],
  );
  const fallbackTab: TabId | null = visible[0]?.id ?? null;
  const requested = params.get("tab") as TabId | null;
  const allowed = requested != null && visible.some((t) => t.id === requested);
  const activeTab: TabId | null = allowed ? requested : fallbackTab;

  // Заход по запрещённому (или отсутствующему) `?tab=` уводит на первую
  // разрешённую. Именно редирект, а не тихая подмена содержимого: иначе
  // адрес врёт о том, что открыто, и такую ссылку отправят дальше.
  useEffect(() => {
    if (allowed || fallbackTab == null) return;
    const next = new URLSearchParams(params);
    next.set("tab", fallbackTab);
    setParams(next, { replace: true });
  }, [allowed, fallbackTab, params, setParams]);

  const setTab = (id: TabId) => {
    const next = new URLSearchParams(params);
    next.set("tab", id);
    setParams(next, { replace: false });
  };

  const orgName = user?.org_name ?? "your organization";

  return (
    <>
      <Topbar
        rightSlot={
          <div
            className="row"
            style={{
              marginLeft: "auto",
              alignItems: "center",
              gap: 12,
            }}
          >
            <UserAlertsBell />
          </div>
        }
      />
      <div className="content">
        <div style={{ marginBottom: 6 }}>
          <h1
            className="serif"
            style={{
              margin: 0,
              fontSize: 32,
              fontWeight: 400,
              letterSpacing: "-0.015em",
            }}
          >
            <em style={{ color: "var(--c-accent-600)", fontStyle: "italic" }}>
              Settings for {orgName}
            </em>
          </h1>
          <div
            className="muted"
            style={{ fontSize: 13.5, marginTop: 6, maxWidth: 720 }}
          >
            Connections, billing data, team and per-app rules. Only
            administrators can modify most of these settings.
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 2,
            borderBottom: "1px solid var(--c-divider)",
            marginBottom: 24,
            marginTop: 18,
            overflowX: "auto",
          }}
        >
          {visible.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`st-tab ${activeTab === t.id ? "active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === "ad-account" && <AdAccountTab />}
        {activeTab === "integrations" && <IntegrationsTab />}
        {activeTab === "roles" && <RolesTeamTab />}
        {activeTab === "organization" && <OrganizationTab />}
        {activeTab === "billing" && <BillingTab />}
        {activeTab === "app-settings" && <AppSettingsTab />}
        {activeTab === "notifications" && <NotificationsTab />}
        {activeTab === "profile" && <ProfileTab />}
        {activeTab === "other" && <OtherTab />}
      </div>
    </>
  );
}

